import { useState } from "react";
import { supabase } from "../../lib/supabase";
import type { Deal, PreInspectionData, TeamMember } from "../../lib/types";
import PreFindingsCard from "./PreFindingsCard";
import { notifyAdjusterReady } from "../../lib/notifyAdjusterReady";

// ---------------------------------------------------------------------------
// Types for each transition form
// ---------------------------------------------------------------------------

export type TransitionType =
  | "claim_called_in"         // Any leads stage → assign_roof_specialist
  | "schedule_presentation"   // initial_contact → presentation_scheduled
  | "presentation_completed"  // presentation_scheduled → follow_up
  | "assign_rs"               // assign_roof_specialist → pre_inspection_ready
  | "pre_done"                // pre_inspection_ready → pre_inspection_complete (notes only, no adj date)
  | "schedule_adjuster"       // pre_inspection_complete → adjuster_meeting_ready (sets the adjuster date)
  | "withdraw_pre"            // pre_inspection_ready → withdraw request
  | "adjuster_done"           // adjuster_meeting_ready → waiting_on_claims
  | "set_expectation_flag"    // flag on waiting_on_claims
  | "insurance_received"      // waiting_on_claims → install_meeting, partial_approval, or reinspection
  | "begin_reinspection_prep" // partial_approval → docs_and_contingency
  | "reinspection_checklist"  // docs_and_contingency → reinspection_ready
  | "email1_sent"             // reinspection_ready → reinspection_filed
  | "reinspection_approved"   // reinspection_filed → install_meeting
  | "schedule_2nd_adjuster"   // reinspection_filed — set 2nd adjuster meeting date
  | "reinspection_denied"     // any reinspection stage → closed_lost
  | "claims_denied";          // any claims stage → closed_lost (denied)

interface Props {
  type: TransitionType;
  deal: Deal;
  member: TeamMember | null;
  members: Record<string, TeamMember>;
  onClose: () => void;
  onComplete: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Main Modal Container
// ---------------------------------------------------------------------------

export default function StageTransitionModal({ type, deal, member, members, onClose, onComplete }: Props) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" />
      {/* Content */}
      <div
        className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {type === "claim_called_in" && <ClaimCalledInForm deal={deal} member={member} onClose={onClose} onComplete={onComplete} />}
        {type === "schedule_presentation" && <SchedulePresentationForm deal={deal} member={member} onClose={onClose} onComplete={onComplete} />}
        {type === "presentation_completed" && <PresentationCompletedForm deal={deal} member={member} onClose={onClose} onComplete={onComplete} />}
        {type === "assign_rs" && <AssignRSForm deal={deal} member={member} members={members} onClose={onClose} onComplete={onComplete} />}
        {type === "pre_done" && <PreDoneForm deal={deal} member={member} onClose={onClose} onComplete={onComplete} />}
        {type === "schedule_adjuster" && <ScheduleAdjusterForm deal={deal} member={member} onClose={onClose} onComplete={onComplete} />}
        {type === "withdraw_pre" && <WithdrawPreForm deal={deal} member={member} onClose={onClose} onComplete={onComplete} />}
        {type === "adjuster_done" && <AdjusterDoneForm deal={deal} member={member} onClose={onClose} onComplete={onComplete} />}
        {type === "set_expectation_flag" && <ExpectationFlagForm deal={deal} member={member} onClose={onClose} onComplete={onComplete} />}
        {type === "insurance_received" && <InsuranceReceivedForm deal={deal} member={member} onClose={onClose} onComplete={onComplete} />}
        {type === "begin_reinspection_prep" && <BeginReinspectionPrepForm deal={deal} member={member} onClose={onClose} onComplete={onComplete} />}
        {type === "reinspection_checklist" && <ReinspectionChecklistForm deal={deal} member={member} onClose={onClose} onComplete={onComplete} />}
        {type === "email1_sent" && <Email1SentForm deal={deal} member={member} onClose={onClose} onComplete={onComplete} />}
        {type === "reinspection_approved" && <ReinspectionApprovedForm deal={deal} member={member} onClose={onClose} onComplete={onComplete} />}
        {type === "schedule_2nd_adjuster" && <Schedule2ndAdjusterForm deal={deal} member={member} onClose={onClose} onComplete={onComplete} />}
        {type === "reinspection_denied" && <ReinspectionDeniedForm deal={deal} member={member} onClose={onClose} onComplete={onComplete} />}
        {type === "claims_denied" && <ClaimsDeniedForm deal={deal} member={member} onClose={onClose} onComplete={onComplete} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared form primitives
// ---------------------------------------------------------------------------

function ModalHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="px-5 pt-5 pb-3 border-b border-slate-100">
      <h2 className="text-lg font-semibold text-bardin-ink">{title}</h2>
      {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
    </div>
  );
}

function ModalFooter({ onClose, onSubmit, submitting, submitLabel, disabled }: {
  onClose: () => void;
  onSubmit: () => void;
  submitting: boolean;
  submitLabel: string;
  disabled?: boolean;
}) {
  return (
    <div className="px-5 py-4 border-t border-slate-100 flex gap-3 justify-end">
      <button onClick={onClose} className="px-4 py-2 text-sm font-medium rounded-lg text-slate-600 hover:bg-slate-100">
        Cancel
      </button>
      <button
        onClick={onSubmit}
        disabled={submitting || disabled}
        className="px-5 py-2 text-sm font-semibold rounded-lg bg-bardin-teal text-white hover:bg-bardin-teal/90 disabled:opacity-50 active:scale-95 transition"
      >
        {submitting ? "Saving…" : submitLabel}
      </button>
    </div>
  );
}

function FormField({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-bardin-teal/30 focus:border-bardin-teal outline-none";
const selectCls = inputCls;

// ---------------------------------------------------------------------------
// 1. Claim Called In — any leads stage → assign_roof_specialist
// ---------------------------------------------------------------------------

function ClaimCalledInForm({ deal, member, onClose, onComplete }: Omit<Props, "type" | "members">) {
  const [insuranceCompany, setInsuranceCompany] = useState(deal.insurance_company ?? "");
  const [claimNumber, setClaimNumber] = useState(deal.claim_number ?? "");
  const [lossType, setLossType] = useState<"loss" | "discovery">("loss");
  const [lossDate, setLossDate] = useState("");
  const [claimNotes, setClaimNotes] = useState(deal.claim_notes ?? "");
  const [adjDate, setAdjDate] = useState("");
  const [adjTime, setAdjTime] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = insuranceCompany.trim() && claimNumber.trim() && lossDate;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);

    const updates: Record<string, unknown> = {
      insurance_company: insuranceCompany.trim(),
      claim_number: claimNumber.trim(),
      claim_notes: claimNotes.trim() || null,
      // The date the user types is the storm / loss-event date, not the call.
      // claim_called_in_date is stamped with today's date because this
      // transition IS the act of filing the claim.
      date_of_loss: lossDate,
      claim_called_in_date: new Date().toISOString().slice(0, 10),
      stage: "assign_roof_specialist",
      phase: "claims",
    };

    // Adjuster date/time optional
    if (adjDate) {
      const dt = adjTime ? `${adjDate}T${adjTime}:00` : `${adjDate}T09:00:00`;
      updates.adjuster_meeting_date = dt;
    }

    const { error } = await supabase.from("deals").update(updates).eq("id", deal.id);
    if (error) { alert(`Error: ${error.message}`); setSubmitting(false); return; }

    await supabase.from("deal_notes").insert({
      deal_id: deal.id,
      author_id: member?.id ?? null,
      note_type: "stage_change",
      content: `Claim called in — ${insuranceCompany}, #${claimNumber}, ${lossType === "loss" ? "Date of Loss" : "Date of Discovery"}: ${lossDate}`,
      metadata: { from_stage: deal.stage, to_stage: "assign_roof_specialist", loss_type: lossType },
    });

    await onComplete();
    onClose();
  }

  return (
    <>
      <ModalHeader title="Claim Called In" subtitle="File the insurance claim and move to Claims phase" />
      <div className="px-5 py-4 space-y-4">
        <FormField label="Insurance Company" required>
          <input className={inputCls} value={insuranceCompany} onChange={(e) => setInsuranceCompany(e.target.value)} placeholder="e.g. State Farm, USAA, Allstate" />
        </FormField>
        <FormField label="Claim Number" required>
          <input className={inputCls} value={claimNumber} onChange={(e) => setClaimNumber(e.target.value)} placeholder="Claim #" />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Type" required>
            <select className={selectCls} value={lossType} onChange={(e) => setLossType(e.target.value as "loss" | "discovery")}>
              <option value="loss">Date of Loss</option>
              <option value="discovery">Date of Discovery</option>
            </select>
          </FormField>
          <FormField label="Date" required>
            <input type="date" className={inputCls} value={lossDate} onChange={(e) => setLossDate(e.target.value)} />
          </FormField>
        </div>
        <FormField label="Claim Notes">
          <textarea className={inputCls + " resize-none"} rows={3} value={claimNotes} onChange={(e) => setClaimNotes(e.target.value)} placeholder="Any notes about this claim…" />
        </FormField>
        <div className="bg-slate-50 rounded-lg p-3">
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Adjuster Meeting (optional)</div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Date">
              <input type="date" className={inputCls} value={adjDate} onChange={(e) => setAdjDate(e.target.value)} />
            </FormField>
            <FormField label="Time">
              <input type="time" className={inputCls} value={adjTime} onChange={(e) => setAdjTime(e.target.value)} />
            </FormField>
          </div>
        </div>
      </div>
      <ModalFooter onClose={onClose} onSubmit={submit} submitting={submitting} submitLabel="File Claim" disabled={!canSubmit} />
    </>
  );
}

// ---------------------------------------------------------------------------
// 2. Schedule Presentation — initial_contact → presentation_scheduled
// ---------------------------------------------------------------------------

function SchedulePresentationForm({ deal, member, onClose, onComplete }: Omit<Props, "type" | "members">) {
  const [presDate, setPresDate] = useState("");
  const [presTime, setPresTime] = useState("");
  const [presType, setPresType] = useState<"live" | "call" | "">("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = presDate && presTime && presType;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);

    const dt = `${presDate}T${presTime}:00`;
    const { error } = await supabase.from("deals").update({
      presentation_date: dt,
      presentation_type: presType,
      stage: "presentation_scheduled",
    }).eq("id", deal.id);
    if (error) { alert(`Error: ${error.message}`); setSubmitting(false); return; }

    await supabase.from("deal_notes").insert({
      deal_id: deal.id,
      author_id: member?.id ?? null,
      note_type: "stage_change",
      content: `Presentation scheduled — ${presType === "live" ? "In-Person" : "Phone Call"} on ${presDate} at ${presTime}`,
      metadata: { from_stage: deal.stage, to_stage: "presentation_scheduled", presentation_type: presType },
    });

    await onComplete();
    onClose();
  }

  return (
    <>
      <ModalHeader title="Schedule Presentation" subtitle="Set a date, time, and type for the homeowner presentation" />
      <div className="px-5 py-4 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Date" required>
            <input type="date" className={inputCls} value={presDate} onChange={(e) => setPresDate(e.target.value)} />
          </FormField>
          <FormField label="Time" required>
            <input type="time" className={inputCls} value={presTime} onChange={(e) => setPresTime(e.target.value)} />
          </FormField>
        </div>
        <FormField label="Presentation Type" required>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setPresType("live")}
              className={`p-3 rounded-lg border-2 text-sm font-medium transition ${presType === "live" ? "border-bardin-teal bg-bardin-teal/5 text-bardin-teal" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
            >
              <div className="text-lg mb-1">🏠</div>
              In-Person (Live)
            </button>
            <button
              onClick={() => setPresType("call")}
              className={`p-3 rounded-lg border-2 text-sm font-medium transition ${presType === "call" ? "border-bardin-teal bg-bardin-teal/5 text-bardin-teal" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
            >
              <div className="text-lg mb-1">📞</div>
              Phone Call
            </button>
          </div>
        </FormField>
      </div>
      <ModalFooter onClose={onClose} onSubmit={submit} submitting={submitting} submitLabel="Schedule" disabled={!canSubmit} />
    </>
  );
}

// ---------------------------------------------------------------------------
// 3. Presentation Completed — presentation_scheduled → follow_up
// ---------------------------------------------------------------------------

function PresentationCompletedForm({ deal, member, onClose, onComplete }: Omit<Props, "type" | "members">) {
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);
    const { error } = await supabase.from("deals").update({ stage: "follow_up" }).eq("id", deal.id);
    if (error) { alert(`Error: ${error.message}`); setSubmitting(false); return; }

    await supabase.from("deal_notes").insert({
      deal_id: deal.id,
      author_id: member?.id ?? null,
      note_type: "stage_change",
      content: `Presentation completed${notes ? ` — ${notes}` : ""}`,
      metadata: { from_stage: "presentation_scheduled", to_stage: "follow_up" },
    });

    await onComplete();
    onClose();
  }

  return (
    <>
      <ModalHeader title="Presentation Completed" subtitle="Mark the presentation as done and move to follow-up" />
      <div className="px-5 py-4 space-y-4">
        <FormField label="Notes (optional)">
          <textarea className={inputCls + " resize-none"} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="How did the presentation go?" />
        </FormField>
      </div>
      <ModalFooter onClose={onClose} onSubmit={submit} submitting={submitting} submitLabel="Complete Presentation" />
    </>
  );
}

// ---------------------------------------------------------------------------
// 4. Assign RS — assign_roof_specialist → pre_inspection_ready
// ---------------------------------------------------------------------------

function AssignRSForm({ deal, member, members, onClose, onComplete }: Omit<Props, "type">) {
  const [rsId, setRsId] = useState(deal.assigned_rs_id ?? "");
  const [submitting, setSubmitting] = useState(false);

  // RS candidates: dedicated RS team (roof_specialist, roof_team_manager, qc, roof_director, director_of_operations)
  // + TL / ASM / RM so a sales person can be held responsible when the roof team isn't available
  const rsCandidates = Object.values(members).filter(
    (m) =>
      m.is_active &&
      (m.role === "roof_specialist" ||
        m.role === "roof_team_manager" ||
        m.role === "qc" ||
        m.role === "roof_director" ||
        m.role === "director_of_operations" ||
        m.role === "team_lead" ||
        m.role === "asm" ||
        m.role === "restore_manager")
  );

  const canSubmit = !!rsId;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);

    const rsName = members[rsId]?.full_name ?? "Unknown";
    const { error } = await supabase.from("deals").update({
      assigned_rs_id: rsId,
      rs_assignment_date: new Date().toISOString(),
      stage: "pre_inspection_ready",
    }).eq("id", deal.id);
    if (error) { alert(`Error: ${error.message}`); setSubmitting(false); return; }

    await supabase.from("deal_notes").insert({
      deal_id: deal.id,
      author_id: member?.id ?? null,
      note_type: "stage_change",
      content: `Roof specialist assigned: ${rsName}`,
      metadata: { from_stage: "assign_roof_specialist", to_stage: "pre_inspection_ready", rs_id: rsId },
    });

    await onComplete();
    onClose();
  }

  return (
    <>
      <ModalHeader title="Assign Responsible Party" subtitle="Pick the RS — or the TL / ASM / RM responsible if the roof team isn't available" />
      <div className="px-5 py-4 space-y-4">
        <FormField label="Responsible Party" required>
          <select className={selectCls} value={rsId} onChange={(e) => setRsId(e.target.value)}>
            <option value="">Select person…</option>
            {rsCandidates.map((m) => (
              <option key={m.id} value={m.id}>{m.full_name}</option>
            ))}
          </select>
        </FormField>
      </div>
      <ModalFooter onClose={onClose} onSubmit={submit} submitting={submitting} submitLabel="Assign" disabled={!canSubmit} />
    </>
  );
}

// ---------------------------------------------------------------------------
// 5. PRE Done — pre_inspection_ready → adjuster_meeting_ready
// ---------------------------------------------------------------------------
// Apr 27 2026: Greg — RS rarely knows the adjuster meeting date at the time
// they finish the pre-inspection. Drop the adjuster-date gate. RS captures
// findings/notes only.
// Jun 8 2026: Greg — collapse the intermediate pre_inspection_complete stage.
// PRE Done now advances straight to adjuster_meeting_ready and fires
// notifyAdjusterReady (in-app, to owner + split RM + assigned RS) flagging
// whether the adjuster meeting date/time is on file yet. The adjuster date is
// still set afterward via the editable field / ScheduleAdjusterForm (the latter
// is kept reachable for any legacy deals still sitting at pre_inspection_complete).

function PreDoneForm({ deal, member, onClose, onComplete }: Omit<Props, "type" | "members">) {
  const existing = deal.pre_inspection_data ?? null;
  const [data, setData] = useState<PreInspectionData>({
    gutter_guards: existing?.gutter_guards ?? null,
    siding_rot: existing?.siding_rot ?? null,
    satellite: existing?.satellite ?? null,
    damage_description: existing?.damage_description ?? null,
    collateral_description: existing?.collateral_description ?? null,
    // Seed notes from legacy text field if no structured data yet
    notes: existing?.notes ?? deal.pre_inspection_findings ?? null,
  });
  const [submitting, setSubmitting] = useState(false);

  function setField<K extends keyof PreInspectionData>(key: K, value: PreInspectionData[K]) {
    setData((prev) => ({ ...prev, [key]: value }));
  }

  // Concatenate description fields into the legacy `pre_inspection_findings`
  // text column so any older readers (RM stage-change notification preview,
  // older list views, deal_notes activity log) still get the gist.
  function buildLegacyText(): string | null {
    const parts: string[] = [];
    const flagYesList = ([
      ["gutter_guards", "Gutter guards"],
      ["siding_rot", "Siding rot"],
      ["satellite", "Satellite"],
    ] as const)
      .filter(([k]) => data[k] === true)
      .map(([, label]) => label);
    if (flagYesList.length > 0) parts.push(`⚠ ${flagYesList.join(", ")}`);
    if (data.damage_description?.trim()) parts.push(`Damage: ${data.damage_description.trim()}`);
    if (data.collateral_description?.trim()) parts.push(`Collateral: ${data.collateral_description.trim()}`);
    if (data.notes?.trim()) parts.push(`Notes: ${data.notes.trim()}`);
    return parts.length ? parts.join("\n") : null;
  }

  function summarize(): string {
    const parts: string[] = [];
    if (data.gutter_guards !== null) parts.push(`gutter guards ${data.gutter_guards ? "Y" : "N"}`);
    if (data.siding_rot !== null) parts.push(`siding rot ${data.siding_rot ? "Y" : "N"}`);
    if (data.satellite !== null) parts.push(`satellite ${data.satellite ? "Y" : "N"}`);
    return parts.join(" · ");
  }

  async function submit() {
    setSubmitting(true);

    // Strip whitespace-only string fields to nulls so they don't take up space
    // in JSONB and so display logic can `if (data.notes)` cleanly.
    const cleaned: PreInspectionData = {
      gutter_guards: data.gutter_guards,
      siding_rot: data.siding_rot,
      satellite: data.satellite,
      damage_description: data.damage_description?.trim() || null,
      collateral_description: data.collateral_description?.trim() || null,
      notes: data.notes?.trim() || null,
    };
    const legacyText = buildLegacyText();

    const { error } = await supabase.from("deals").update({
      pre_inspection_completed: true,
      pre_inspection_completed_date: new Date().toISOString(),
      pre_inspection_data: cleaned,
      pre_inspection_findings: legacyText, // back-compat for older readers
      stage: "adjuster_meeting_ready",
    }).eq("id", deal.id);
    if (error) { alert(`Error: ${error.message}`); setSubmitting(false); return; }

    const summary = summarize();
    await supabase.from("deal_notes").insert({
      deal_id: deal.id,
      author_id: member?.id ?? null,
      note_type: "stage_change",
      content: legacyText
        ? `Pre-inspection completed${summary ? ` (${summary})` : ""}. Findings:\n${legacyText}`
        : `Pre-inspection completed. (No findings flagged.)`,
      metadata: { from_stage: "pre_inspection_ready", to_stage: "adjuster_meeting_ready" },
    });

    // Notify RM + split RM + assigned RS that the deal is ready for an adjuster
    // meeting, flagging whether the date/time is already set. Fire-and-forget.
    void notifyAdjusterReady(deal);

    await onComplete();
    onClose();
  }

  const yesCount = (data.gutter_guards === true ? 1 : 0)
    + (data.siding_rot === true ? 1 : 0)
    + (data.satellite === true ? 1 : 0);

  return (
    <>
      <ModalHeader title="PRE Complete" subtitle="Capture findings for the RM, Chad's approval, and the install crew." />
      <div className="px-5 py-4 space-y-4">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
          <span className="text-emerald-600 text-lg">✓</span>
          <span className="text-sm font-medium text-emerald-700">Pre-inspection complete → moves to Adjuster Meeting Ready</span>
        </div>

        {/* Y/N flags — explicitly amber when Yes so they can't be missed downstream */}
        <div className="space-y-2">
          <YnRow label="Gutter Guards" value={data.gutter_guards} onChange={(v) => setField("gutter_guards", v)} hint="If yes, must be removed before tear-off." />
          <YnRow label="Siding Rot"    value={data.siding_rot}    onChange={(v) => setField("siding_rot", v)}    hint="If yes, scope discussion needed before build day." />
          <YnRow label="Satellite"     value={data.satellite}     onChange={(v) => setField("satellite", v)}     hint="If yes, schedule relocation before tear-off." />
        </div>

        {yesCount > 0 && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-2.5 text-xs text-amber-800">
            ⚠ {yesCount} item{yesCount === 1 ? "" : "s"} flagged. Chad, Robert, and the install crew will see these on every downstream card.
          </div>
        )}

        <FormField label="Description of damage">
          <textarea
            className={inputCls}
            rows={3}
            value={data.damage_description ?? ""}
            onChange={(e) => setField("damage_description", e.target.value)}
            placeholder="What did the storm do? Slopes affected, hail size, wind impact…"
          />
        </FormField>

        <FormField label="Collateral description">
          <textarea
            className={inputCls}
            rows={2}
            value={data.collateral_description ?? ""}
            onChange={(e) => setField("collateral_description", e.target.value)}
            placeholder="Dented gutters, broken vents, fascia damage, screens, AC fins…"
          />
        </FormField>

        <FormField label="Notes">
          <textarea
            className={inputCls}
            rows={3}
            value={data.notes ?? ""}
            onChange={(e) => setField("notes", e.target.value)}
            placeholder="Anything else the RM, Chad, or the install crew needs to know."
          />
        </FormField>

        <p className="text-xs text-slate-500">
          All fields are optional but everything you flag here surfaces on Chad's approval card, Robert's schedule, and the install workbook on build day.
        </p>
      </div>
      <ModalFooter onClose={onClose} onSubmit={submit} submitting={submitting} submitLabel="PRE Done" />
    </>
  );
}

/** 3-button Yes / No / Unset row for the PRE form. */
function YnRow({ label, value, onChange, hint }: {
  label: string;
  value: boolean | null;
  onChange: (next: boolean | null) => void;
  hint?: string;
}) {
  const baseBtn = "px-3 py-1.5 text-xs font-semibold rounded border transition-colors";
  return (
    <div className="border border-slate-200 rounded-lg p-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-bardin-ink">{label}</div>
          {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
        </div>
        <div className="flex gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => onChange(value === true ? null : true)}
            className={`${baseBtn} ${value === true ? "bg-amber-100 border-amber-400 text-amber-800" : "bg-white border-slate-300 text-slate-600 hover:border-amber-300 hover:text-amber-700"}`}
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => onChange(value === false ? null : false)}
            className={`${baseBtn} ${value === false ? "bg-slate-200 border-slate-400 text-slate-700" : "bg-white border-slate-300 text-slate-600 hover:border-slate-400"}`}
          >
            No
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 5b. Schedule Adjuster — pre_inspection_complete → adjuster_meeting_ready
// ---------------------------------------------------------------------------
// Splits the formerly-bundled "set adjuster date" out of PreDoneForm. RM
// (or RS, once known) calls this once the insurance carrier confirms when
// the adjuster will be on site.

function ScheduleAdjusterForm({ deal, member, onClose, onComplete }: Omit<Props, "type" | "members">) {
  const [adjDate, setAdjDate] = useState(deal.adjuster_meeting_date?.slice(0, 10) ?? "");
  const [adjTime, setAdjTime] = useState(deal.adjuster_meeting_date?.slice(11, 16) ?? "");
  const [adjName, setAdjName] = useState(deal.adjuster_name ?? "");
  const [adjPhone, setAdjPhone] = useState(deal.adjuster_phone ?? "");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = adjDate && adjTime;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);

    const dt = `${adjDate}T${adjTime}:00`;
    const { error } = await supabase.from("deals").update({
      adjuster_meeting_date: dt,
      adjuster_name: adjName.trim() || null,
      adjuster_phone: adjPhone.trim() || null,
      stage: "adjuster_meeting_ready",
    }).eq("id", deal.id);
    if (error) { alert(`Error: ${error.message}`); setSubmitting(false); return; }

    await supabase.from("deal_notes").insert({
      deal_id: deal.id,
      author_id: member?.id ?? null,
      note_type: "stage_change",
      content: `Adjuster meeting scheduled for ${adjDate} at ${adjTime}${adjName ? ` (${adjName.trim()})` : ""}`,
      metadata: { from_stage: "pre_inspection_complete", to_stage: "adjuster_meeting_ready" },
    });

    await onComplete();
    onClose();
  }

  return (
    <>
      <ModalHeader title="Schedule Adjuster Meeting" subtitle="Log the date the adjuster will be on site." />
      <div className="px-5 py-4 space-y-4">
        <PreFindingsCard deal={deal} />
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Adjuster Meeting Date" required>
            <input type="date" className={inputCls} value={adjDate} onChange={(e) => setAdjDate(e.target.value)} />
          </FormField>
          <FormField label="Start time" required>
            <input type="time" className={inputCls} value={adjTime} onChange={(e) => setAdjTime(e.target.value)} />
          </FormField>
        </div>
        <p className="text-xs text-slate-500 -mt-2">
          Insurance usually gives a window (e.g., 10am–1pm). Enter the <span className="font-medium">start</span> — adjusters typically arrive at that time. Can be updated later.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Adjuster name">
            <input type="text" className={inputCls} placeholder="e.g. Mike Johnson" value={adjName} onChange={(e) => setAdjName(e.target.value)} />
          </FormField>
          <FormField label="Adjuster phone">
            <input type="tel" className={inputCls} placeholder="(770) 555-0000" value={adjPhone} onChange={(e) => setAdjPhone(e.target.value)} />
          </FormField>
        </div>
      </div>
      <ModalFooter onClose={onClose} onSubmit={submit} submitting={submitting} submitLabel="Schedule Meeting" disabled={!canSubmit} />
    </>
  );
}

// ---------------------------------------------------------------------------
// 6. Withdraw PRE — RS requests withdraw from pre_inspection_ready
// ---------------------------------------------------------------------------

function WithdrawPreForm({ deal, member, onClose, onComplete }: Omit<Props, "type" | "members">) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = reason.trim().length > 0;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);

    const { error } = await supabase.from("deals").update({
      withdraw_status: "pending",
      withdraw_reason: reason.trim(),
      withdraw_requested_at: new Date().toISOString(),
    }).eq("id", deal.id);
    if (error) { alert(`Error: ${error.message}`); setSubmitting(false); return; }

    await supabase.from("deal_notes").insert({
      deal_id: deal.id,
      author_id: member?.id ?? null,
      note_type: "withdraw_request",
      content: `Withdraw requested — ${reason.trim()}`,
      metadata: { stage: deal.stage, requested_by: member?.id },
    });

    await onComplete();
    onClose();
  }

  return (
    <>
      <ModalHeader title="Request Withdraw" subtitle="Submit a withdrawal request — Jennifer must approve" />
      <div className="px-5 py-4 space-y-4">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <span className="text-sm text-amber-700">This deal will be flagged for Jennifer's review. She will approve or reject the withdraw.</span>
        </div>
        <FormField label="Reason for Withdraw" required>
          <textarea className={inputCls + " resize-none"} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why should this deal be withdrawn?" />
        </FormField>
      </div>
      <ModalFooter onClose={onClose} onSubmit={submit} submitting={submitting} submitLabel="Submit Withdraw" disabled={!canSubmit} />
    </>
  );
}

// ---------------------------------------------------------------------------
// 7. Adjuster Done — adjuster_meeting_ready → waiting_on_claims
// ---------------------------------------------------------------------------

// Common loss-adjuster / ladder-assist firms. Dropdown choices on the
// Adjuster Meeting Done form. "Other" lets the RM type a one-off.
const LA_COMPANY_OPTIONS = ["Seek Now", "Pilot", "Patriot", "Superior", "M&A", "Hancock"] as const;

function AdjusterDoneForm({ deal, member, onClose, onComplete }: Omit<Props, "type" | "members">) {
  const [notes, setNotes] = useState(deal.adjuster_meeting_outcome ?? "");
  const [adjusterPresent, setAdjusterPresent] = useState<boolean | null>(deal.adjuster_present ?? null);

  // Existing la_company value: pre-pick from dropdown if it's one of the known
  // names, otherwise put it in the "Other" free-text box.
  const existingLa = deal.la_company ?? "";
  const isKnownLa = (LA_COMPANY_OPTIONS as readonly string[]).includes(existingLa);
  const [laChoice, setLaChoice] = useState<string>(isKnownLa ? existingLa : (existingLa ? "Other" : ""));
  const [laOther, setLaOther] = useState<string>(isKnownLa ? "" : existingLa);

  const [submitting, setSubmitting] = useState(false);

  const canSubmit = notes.trim().length > 0;

  function resolvedLaCompany(): string | null {
    if (!laChoice) return null;
    if (laChoice === "Other") return laOther.trim() || null;
    return laChoice;
  }

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);

    const laCompany = resolvedLaCompany();

    const { error } = await supabase.from("deals").update({
      adjuster_meeting_outcome: notes.trim(),
      adjuster_present: adjusterPresent,
      la_company: laCompany,
      stage: "waiting_on_claims",
    }).eq("id", deal.id);
    if (error) { alert(`Error: ${error.message}`); setSubmitting(false); return; }

    // Build a one-line summary for the activity log so reviewers don't have to
    // open the deal to see the highlights.
    const summary: string[] = [];
    if (adjusterPresent !== null) summary.push(`adjuster ${adjusterPresent ? "present" : "no-show"}`);
    if (laCompany) summary.push(`LA: ${laCompany}`);
    const summaryStr = summary.length ? ` (${summary.join(" · ")})` : "";

    await supabase.from("deal_notes").insert({
      deal_id: deal.id,
      author_id: member?.id ?? null,
      note_type: "stage_change",
      content: `Adjuster meeting completed${summaryStr} — ${notes.trim()}`,
      metadata: {
        from_stage: "adjuster_meeting_ready",
        to_stage: "waiting_on_claims",
        adjuster_present: adjusterPresent,
        la_company: laCompany,
      },
    });

    await onComplete();
    onClose();
  }

  const baseYn = "px-3 py-1.5 text-xs font-semibold rounded border transition-colors";

  return (
    <>
      <ModalHeader title="Adjuster Meeting Done" subtitle="Record how the meeting went" />
      <div className="px-5 py-4 space-y-4">
        {/* Adjuster present Y/N */}
        <div className="border border-slate-200 rounded-lg p-2.5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-bardin-ink">Adjuster present?</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                Did the carrier adjuster actually show up?
              </div>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => setAdjusterPresent(adjusterPresent === true ? null : true)}
                className={`${baseYn} ${adjusterPresent === true ? "bg-emerald-100 border-emerald-400 text-emerald-800" : "bg-white border-slate-300 text-slate-600 hover:border-emerald-300 hover:text-emerald-700"}`}
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => setAdjusterPresent(adjusterPresent === false ? null : false)}
                className={`${baseYn} ${adjusterPresent === false ? "bg-amber-100 border-amber-400 text-amber-800" : "bg-white border-slate-300 text-slate-600 hover:border-amber-300 hover:text-amber-700"}`}
              >
                No
              </button>
            </div>
          </div>
        </div>

        {/* LA company dropdown + free-text fallback for "Other" */}
        <FormField label="LA company">
          <select
            className={inputCls}
            value={laChoice}
            onChange={(e) => setLaChoice(e.target.value)}
          >
            <option value="">— None / not applicable —</option>
            {LA_COMPANY_OPTIONS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
            <option value="Other">Other (type below)</option>
          </select>
          {laChoice === "Other" && (
            <input
              className={`${inputCls} mt-2`}
              type="text"
              value={laOther}
              onChange={(e) => setLaOther(e.target.value)}
              placeholder="LA / ladder-assist firm name"
            />
          )}
        </FormField>

        <FormField label="Meeting Notes" required>
          <textarea
            className={inputCls + " resize-none"}
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="How did the adjuster meeting go? What was the outcome?"
          />
        </FormField>
      </div>
      <ModalFooter onClose={onClose} onSubmit={submit} submitting={submitting} submitLabel="Meeting Done" disabled={!canSubmit} />
    </>
  );
}

// ---------------------------------------------------------------------------
// 8. Expectation Flag — toggle on waiting_on_claims deals
// ---------------------------------------------------------------------------

function ExpectationFlagForm({ deal, member, onClose, onComplete }: Omit<Props, "type" | "members">) {
  const [flag, setFlag] = useState<"expected_approval" | "expected_reinspection" | "">(
    (deal.claims_expectation_flag as "expected_approval" | "expected_reinspection") ?? ""
  );
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);
    const { error } = await supabase.from("deals").update({
      claims_expectation_flag: flag || null,
    }).eq("id", deal.id);
    if (error) { alert(`Error: ${error.message}`); setSubmitting(false); return; }

    await supabase.from("deal_notes").insert({
      deal_id: deal.id,
      author_id: member?.id ?? null,
      note_type: "flag_update",
      content: flag ? `Expectation set: ${flag === "expected_approval" ? "Expected Approval" : "Expected Reinspection"}` : "Expectation flag cleared",
      metadata: { flag },
    });

    await onComplete();
    onClose();
  }

  return (
    <>
      <ModalHeader title="Set Expectation" subtitle="What do you expect from insurance?" />
      <div className="px-5 py-4 space-y-3">
        <button
          onClick={() => setFlag("expected_approval")}
          className={`w-full p-4 rounded-lg border-2 text-left transition ${flag === "expected_approval" ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
        >
          <div className="font-medium text-sm text-emerald-700">✓ Expected Approval</div>
          <div className="text-xs text-slate-500 mt-0.5">Insurance likely to approve the full claim</div>
        </button>
        <button
          onClick={() => setFlag("expected_reinspection")}
          className={`w-full p-4 rounded-lg border-2 text-left transition ${flag === "expected_reinspection" ? "border-purple-500 bg-purple-50" : "border-slate-200 hover:border-slate-300"}`}
        >
          <div className="font-medium text-sm text-purple-700">⟳ Expected Reinspection</div>
          <div className="text-xs text-slate-500 mt-0.5">Likely headed to reinspection process</div>
        </button>
        {flag && (
          <button onClick={() => setFlag("")} className="text-xs text-slate-500 hover:text-slate-700 underline">
            Clear flag
          </button>
        )}
      </div>
      <ModalFooter onClose={onClose} onSubmit={submit} submitting={submitting} submitLabel="Save" />
    </>
  );
}

// ---------------------------------------------------------------------------
// 9. Insurance Received — waiting_on_claims → install_meeting or reinspection
// ---------------------------------------------------------------------------

function InsuranceReceivedForm({ deal, member, onClose, onComplete }: Omit<Props, "type" | "members">) {
  const [destination, setDestination] = useState<"install_meeting" | "partial_approval" | "docs_and_contingency" | "">("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = !!destination;

  const LABELS: Record<string, string> = {
    install_meeting: "Builds (Install Meeting)",
    partial_approval: "Reinspections (Partial Approval)",
    docs_and_contingency: "Reinspections (Denied — Docs & Contingency)",
  };

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);

    const newPhase = destination === "install_meeting" ? "builds" : "reinspections";
    const isPartial = destination === "partial_approval";
    const { error } = await supabase.from("deals").update({
      insurance_paperwork_uploaded: true,
      stage: destination,
      phase: newPhase,
      // Durable flag — keeps the deal badged + sorted to the front of the
      // reinspection queue through docs/ready/filed. Cleared on the other routes.
      reinspection_partial_approval: isPartial,
    }).eq("id", deal.id);
    if (error) { alert(`Error: ${error.message}`); setSubmitting(false); return; }

    await supabase.from("deal_notes").insert({
      deal_id: deal.id,
      author_id: member?.id ?? null,
      note_type: "stage_change",
      content: `Insurance paperwork received — routed to ${LABELS[destination]}`,
      metadata: { from_stage: "waiting_on_claims", to_stage: destination, phase: newPhase, partial_approval: isPartial },
    });

    await onComplete();
    onClose();
  }

  return (
    <>
      <ModalHeader title="Insurance Paperwork Received" subtitle="Where does this deal go next? ASM will review." />
      <div className="px-5 py-4 space-y-3">
        <button
          onClick={() => setDestination("install_meeting")}
          className={`w-full p-4 rounded-lg border-2 text-left transition ${destination === "install_meeting" ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
        >
          <div className="font-semibold text-sm text-emerald-700">✓ Fully Approved — Send to Builds</div>
          <div className="text-xs text-slate-500 mt-0.5">Insurance approved the full scope. Move to Install Meeting in Builds phase.</div>
        </button>
        <button
          onClick={() => setDestination("partial_approval")}
          className={`w-full p-4 rounded-lg border-2 text-left transition ${destination === "partial_approval" ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}
        >
          <div className="font-semibold text-sm text-amber-700">◑ Partial Approval — Reinspect</div>
          <div className="text-xs text-slate-500 mt-0.5">Insurance approved some items but not the full roof. Sent to Reinspections and flagged to the front of the line.</div>
        </button>
        <button
          onClick={() => setDestination("docs_and_contingency")}
          className={`w-full p-4 rounded-lg border-2 text-left transition ${destination === "docs_and_contingency" ? "border-purple-500 bg-purple-50" : "border-slate-200 hover:border-slate-300"}`}
        >
          <div className="font-semibold text-sm text-purple-700">✗ Denied — Reinspect</div>
          <div className="text-xs text-slate-500 mt-0.5">Insurance denied the claim. Send to Reinspections to gather docs &amp; contingency.</div>
        </button>
      </div>
      <ModalFooter onClose={onClose} onSubmit={submit} submitting={submitting} submitLabel="Route Deal" disabled={!canSubmit} />
    </>
  );
}

// ---------------------------------------------------------------------------
// 9b. Begin Reinspection Prep — partial_approval → docs_and_contingency
// ---------------------------------------------------------------------------

function BeginReinspectionPrepForm({ deal, member, onClose, onComplete }: Omit<Props, "type" | "members">) {
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);
    // Keep reinspection_partial_approval = true so the deal stays badged +
    // prioritized through the rest of the reinspection lifecycle.
    const { error } = await supabase.from("deals").update({
      stage: "docs_and_contingency",
      phase: "reinspections",
    }).eq("id", deal.id);
    if (error) { alert(`Error: ${error.message}`); setSubmitting(false); return; }

    await supabase.from("deal_notes").insert({
      deal_id: deal.id,
      author_id: member?.id ?? null,
      note_type: "stage_change",
      content: "Partial approval — started reinspection prep (Docs & Contingency)",
      metadata: { from_stage: "partial_approval", to_stage: "docs_and_contingency", phase: "reinspections" },
    });

    await onComplete();
    onClose();
  }

  return (
    <>
      <ModalHeader title="Begin Reinspection Prep" subtitle="Partial approval — start gathering the reinspection documents." />
      <div className="px-5 py-4">
        <div className="rounded-lg border-2 border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          This deal was a <strong>partial approval</strong>. Moving it to <strong>Docs &amp; Contingency</strong> begins the standard reinspection prep (contingency, Hover, Apex full-roof estimate). It stays flagged as a partial approval and prioritized at the front of the reinspection queue.
        </div>
      </div>
      <ModalFooter onClose={onClose} onSubmit={submit} submitting={submitting} submitLabel="Move to Docs & Contingency" disabled={false} />
    </>
  );
}

// ---------------------------------------------------------------------------
// 10. Reinspection Checklist — docs_and_contingency → reinspection_ready
// ---------------------------------------------------------------------------

function ReinspectionChecklistForm({ deal, member, onClose, onComplete }: Omit<Props, "type" | "members">) {
  const [contingency, setContingency] = useState(!!deal.contingency_signed);
  const [insurance, setInsurance] = useState(!!deal.insurance_paperwork_uploaded);
  const [hoVerified, setHoVerified] = useState(!!deal.ho_info_verified);
  const [hover, setHover] = useState(!!deal.hover_ordered);
  const [apex, setApex] = useState(!!deal.apex_estimate_generated);
  const [submitting, setSubmitting] = useState(false);

  const allChecked = contingency && insurance && hoVerified && hover && apex;

  async function submit() {
    if (!allChecked) return;
    setSubmitting(true);

    const { error } = await supabase.from("deals").update({
      contingency_signed: true,
      insurance_paperwork_uploaded: true,
      ho_info_verified: true,
      hover_ordered: true,
      apex_estimate_generated: true,
      stage: "reinspection_ready",
    }).eq("id", deal.id);
    if (error) { alert(`Error: ${error.message}`); setSubmitting(false); return; }

    await supabase.from("deal_notes").insert({
      deal_id: deal.id,
      author_id: member?.id ?? null,
      note_type: "stage_change",
      content: "Reinspection checklist complete — moved to Reinspection Ready",
      metadata: { from_stage: "docs_and_contingency", to_stage: "reinspection_ready" },
    });

    await onComplete();
    onClose();
  }

  const items = [
    { label: "Signed contingency from homeowner", checked: contingency, toggle: () => setContingency(!contingency) },
    { label: "Insurance paperwork uploaded", checked: insurance, toggle: () => setInsurance(!insurance) },
    { label: "Homeowner phone, email, name verified", checked: hoVerified, toggle: () => setHoVerified(!hoVerified) },
    { label: "Hover measurements ordered", checked: hover, toggle: () => setHover(!hover) },
    { label: "Apex full-roof estimate generated", checked: apex, toggle: () => setApex(!apex) },
  ];

  return (
    <>
      <ModalHeader title="Reinspection Checklist" subtitle="All items must be complete before filing reinspection" />
      <div className="px-5 py-4 space-y-2">
        {items.map((item) => (
          <button
            key={item.label}
            onClick={item.toggle}
            className={`w-full flex items-center gap-3 p-3 rounded-lg border transition text-left ${item.checked ? "border-emerald-200 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
          >
            <span className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${item.checked ? "bg-emerald-500 text-white" : "border-2 border-slate-300"}`}>
              {item.checked && <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
            </span>
            <span className={`text-sm ${item.checked ? "text-emerald-700 font-medium" : "text-slate-600"}`}>{item.label}</span>
          </button>
        ))}
      </div>
      <ModalFooter onClose={onClose} onSubmit={submit} submitting={submitting} submitLabel="Ready for Reinspection" disabled={!allChecked} />
    </>
  );
}

// ---------------------------------------------------------------------------
// 11. Email 1 Sent — reinspection_ready → reinspection_filed
// ---------------------------------------------------------------------------

function Email1SentForm({ deal, member, onClose, onComplete }: Omit<Props, "type" | "members">) {
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);
    const { error } = await supabase.from("deals").update({
      reinspection_email1_sent_at: new Date().toISOString(),
      stage: "reinspection_filed",
    }).eq("id", deal.id);
    if (error) { alert(`Error: ${error.message}`); setSubmitting(false); return; }

    await supabase.from("deal_notes").insert({
      deal_id: deal.id,
      author_id: member?.id ?? null,
      note_type: "stage_change",
      content: "Reinspection Email 1 sent to insurance",
      metadata: { from_stage: "reinspection_ready", to_stage: "reinspection_filed" },
    });

    await onComplete();
    onClose();
  }

  return (
    <>
      <ModalHeader title="Reinspection Email 1 Sent" subtitle="Confirm the first reinspection email has been sent to insurance" />
      <div className="px-5 py-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-sm text-blue-800 font-medium">Confirm that Email 1 has been sent to:</div>
          <ul className="text-sm text-blue-700 mt-2 space-y-1">
            <li>• Insurance claims representative</li>
            <li>• Homeowner</li>
            <li>• RM ({deal.homeowner_name})</li>
          </ul>
        </div>
      </div>
      <ModalFooter onClose={onClose} onSubmit={submit} submitting={submitting} submitLabel="Confirm Email Sent" />
    </>
  );
}

// ---------------------------------------------------------------------------
// 12. Reinspection Approved — reinspection_filed → install_meeting (builds)
// ---------------------------------------------------------------------------

function ReinspectionApprovedForm({ deal, member, onClose, onComplete }: Omit<Props, "type" | "members">) {
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);
    const { error } = await supabase.from("deals").update({
      insurance_paperwork_uploaded: true,
      stage: "install_meeting",
      phase: "builds",
    }).eq("id", deal.id);
    if (error) { alert(`Error: ${error.message}`); setSubmitting(false); return; }

    await supabase.from("deal_notes").insert({
      deal_id: deal.id,
      author_id: member?.id ?? null,
      note_type: "stage_change",
      content: "Insurance scope uploaded after reinspection — moved to Install Meeting (Builds)",
      metadata: { from_stage: "reinspection_filed", to_stage: "install_meeting", phase: "builds" },
    });

    await onComplete();
    onClose();
  }

  return (
    <>
      <ModalHeader title="Insurance Approved (Post-Reinspection)" subtitle="Confirm the scope is uploaded and move to Builds" />
      <div className="px-5 py-4">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="text-sm text-emerald-800 font-medium">This will:</div>
          <ul className="text-sm text-emerald-700 mt-2 space-y-1">
            <li>• Mark insurance paperwork as uploaded</li>
            <li>• Move the deal to Install Meeting in Builds phase</li>
          </ul>
        </div>
      </div>
      <ModalFooter onClose={onClose} onSubmit={submit} submitting={submitting} submitLabel="Move to Builds" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Schedule 2nd Adjuster Meeting (reinspection_filed)
// ---------------------------------------------------------------------------

function Schedule2ndAdjusterForm({ deal, member, onClose, onComplete }: Omit<Props, "type" | "members">) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!date) { alert("Please pick a date."); return; }
    setSubmitting(true);

    // Store as timestamptz — combine date + time (treated as-entered, matching Zoho convention)
    const ts = `${date}T${time}:00+00:00`;

    const { error } = await supabase.from("deals").update({
      reinspection_adjuster_meeting_date: ts,
    }).eq("id", deal.id);
    if (error) { alert(`Error: ${error.message}`); setSubmitting(false); return; }

    await supabase.from("deal_notes").insert({
      deal_id: deal.id,
      author_id: member?.id ?? null,
      note_type: "schedule",
      content: `2nd adjuster meeting scheduled for ${date} at ${time}`,
      metadata: { reinspection_adjuster_meeting_date: ts },
    });

    await onComplete();
    onClose();
  }

  return (
    <>
      <ModalHeader title="Schedule 2nd Adjuster Meeting" subtitle="Set the reinspection adjuster meeting date & time" />
      <div className="px-5 py-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Time</label>
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        {deal.reinspection_adjuster_meeting_date && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            Currently scheduled: {deal.reinspection_adjuster_meeting_date.slice(0, 10)} at {deal.reinspection_adjuster_meeting_date.slice(11, 16)}
          </div>
        )}
      </div>
      <ModalFooter onClose={onClose} onSubmit={submit} submitting={submitting} submitLabel="Schedule Meeting" disabled={!date} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Reinspection Denied → Closed Lost
// ---------------------------------------------------------------------------

function ClaimsDeniedForm({ deal, member, onClose, onComplete }: Omit<Props, "type" | "members">) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);

    const { error } = await supabase.from("deals").update({
      stage: "claims_denied",
      phase: "claims",
      cancellation_date: new Date().toISOString(),
      cancellation_reason: reason || "Insurance claim denied",
      stage_at_cancellation: deal.stage,
      cancellation_requested_by: member?.id ?? null,
    }).eq("id", deal.id);
    if (error) { alert(`Error: ${error.message}`); setSubmitting(false); return; }

    await supabase.from("deal_notes").insert({
      deal_id: deal.id,
      author_id: member?.id ?? null,
      note_type: "stage_change",
      content: `Claim denied — deal closed lost. ${reason ? "Reason: " + reason : ""}`.trim(),
      metadata: { from_stage: deal.stage, to_stage: "claims_denied", phase: "claims", denial: true },
    });

    await onComplete();
    onClose();
  }

  return (
    <>
      <ModalHeader title="Claim Denied — Close Lost" subtitle="Insurance denied the claim — close this deal" />
      <div className="px-5 py-4 space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-sm text-red-800 font-medium">This will close the deal as Denied.</div>
          <div className="text-sm text-red-700 mt-1">The deal stays in Claims for visibility but is marked closed lost.</div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Reason (optional)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Insufficient damage, cosmetic only, pre-existing condition"
            rows={3}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
          />
        </div>
      </div>
      <ModalFooter onClose={onClose} onSubmit={submit} submitting={submitting} submitLabel="Close as Denied" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Reinspection Denied → Closed Lost
// ---------------------------------------------------------------------------

function ReinspectionDeniedForm({ deal, member, onClose, onComplete }: Omit<Props, "type" | "members">) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);

    const { error } = await supabase.from("deals").update({
      stage: "closed_lost",
      phase: "closed",
      cancellation_date: new Date().toISOString(),
      cancellation_reason: reason || "Reinspection denied by insurance",
      stage_at_cancellation: deal.stage,
      cancellation_requested_by: member?.id ?? null,
    }).eq("id", deal.id);
    if (error) { alert(`Error: ${error.message}`); setSubmitting(false); return; }

    await supabase.from("deal_notes").insert({
      deal_id: deal.id,
      author_id: member?.id ?? null,
      note_type: "stage_change",
      content: `Reinspection denied — deal closed lost. ${reason ? "Reason: " + reason : ""}`.trim(),
      metadata: { from_stage: deal.stage, to_stage: "closed_lost", phase: "closed", denial: true },
    });

    await onComplete();
    onClose();
  }

  return (
    <>
      <ModalHeader title="Reinspection Denied" subtitle="Insurance denied the reinspection — close this deal as lost" />
      <div className="px-5 py-4 space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-sm text-red-800 font-medium">This will permanently close the deal as Lost.</div>
          <div className="text-sm text-red-700 mt-1">The deal will be removed from the active pipeline.</div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Reason (optional)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Insurance upheld denial after 2nd adjuster meeting"
            rows={3}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
          />
        </div>
      </div>
      <ModalFooter onClose={onClose} onSubmit={submit} submitting={submitting} submitLabel="Close as Lost" />
    </>
  );
}
